import {
	CircularProgress,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	useTheme,
} from "@mui/material";
import type { LucideIcon } from "lucide-react";
import type { FC } from "react";
import type { ActionsDrawerVariant } from "web_myplann/Infrastructure/Themes/Colors";
import { getActionsDrawerRowColors } from "web_myplann/Shared/Components/Redesign/getActionsDrawerRowColors";

export type DrawerAction = {
	key: string;
	label: string;
	icon: LucideIcon;
	onClick: () => void;
	isDestructive?: boolean;
	isPending?: boolean;
	disabled?: boolean;
	hidden?: boolean;
};

type Props = {
	action: DrawerAction;
	isLast: boolean;
	variant: ActionsDrawerVariant;
};

export const ActionsDrawerRow: FC<Props> = ({ action, isLast, variant }) => {
	const theme = useTheme();
	const { iconSize } = theme.actionsDrawer;
	const variantConfig = theme.actionsDrawer[variant];

	const {
		label,
		icon: Icon,
		onClick,
		isDestructive = false,
		isPending = false,
		disabled = false,
	} = action;
	const isDisabled = disabled || isPending;

	const colors = getActionsDrawerRowColors({
		palette: theme.palette,
		variantConfig,
		isDisabled,
		isDestructive,
	});

	return (
		<ListItemButton
			disableRipple
			disabled={isDisabled}
			divider={variantConfig.hasDivider && !isLast}
			onClick={(event) => {
				event.stopPropagation();
				onClick();
			}}
			sx={{
				gap: theme.spacingTokens.md,
				px: theme.spacingTokens.lg,
				py: theme.spacingTokens.md,
				minHeight: variantConfig.rowMinHeight,
				borderColor: theme.palette.surface.border,
				"&:active": { backgroundColor: variantConfig.pressedBackground },
				"&.Mui-disabled": { opacity: 1 },
			}}
		>
			<ListItemIcon sx={{ minWidth: 0 }}>
				{isPending ? (
					<CircularProgress
						size={iconSize}
						sx={{ color: theme.palette.blue[600] }}
					/>
				) : (
					<Icon size={iconSize} color={colors.icon} />
				)}
			</ListItemIcon>
			<ListItemText
				primary={label}
				slotProps={{
					primary: { variant: "bodyMedium", color: colors.content },
				}}
				sx={{ my: 0 }}
			/>
		</ListItemButton>
	);
};
